import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { PlugZap, Lock, ShieldAlert, CheckCircle2, AlertCircle, HelpCircle, SlidersHorizontal, Save, Trash2 } from "lucide-react";

const STATUS_STYLE: Record<string, string> = {
  Disabled: "bg-slate-100 text-slate-600 border-slate-200",
  Enabled: "bg-emerald-50 text-emerald-800 border-emerald-300",
  "Unavailable / Not Supported": "bg-red-50 text-red-700 border-red-300",
};

const POLICY_FIELDS: { key: string; label: string; type: "text" | "textarea" | "select" | "date"; options?: string[]; tip: string }[] = [
  { key: "authorisation_reference", label: "Authorisation reference", type: "text", tip: "Proof you're permitted to use this source — a licence, terms-of-service or agreement reference. Prevents using a source you have no right to." },
  { key: "source_owner", label: "Source owner", type: "text", tip: "Who owns or controls this data, so accountability for its use is clear." },
  { key: "authentication_method", label: "Authentication method", type: "select", options: ["Public / open data", "API key (licensed)", "OAuth (licensed)", "Licensed data feed", "None required"], tip: "How access is obtained — ensures no credentialed or paywalled source is automated without the right to do so." },
  { key: "permitted_purpose", label: "Permitted purpose", type: "select", options: ["Lead prioritisation & market intelligence", "Company qualification", "Signal monitoring", "Research only"], tip: "The specific lawful reason for collecting this data — underpins privacy compliance (e.g. Australian Privacy Principles)." },
  { key: "retention_period", label: "Retention period", type: "text", tip: "How long collected data may be kept before it must be reviewed or destroyed (e.g. \"12 months\"). Stops indefinite hoarding of third-party data." },
  { key: "attribution", label: "Attribution", type: "text", tip: "How provenance is recorded on every piece of evidence — the backbone of the claim-safe, fully-attributable scoring model." },
  { key: "onward_use_rights", label: "Onward-use rights", type: "select", options: ["Internal use only", "Internal + export permitted", "No onward use"], tip: "Whether findings from this source may be shared or exported beyond this system." },
  { key: "deletion_obligations", label: "Deletion obligations", type: "textarea", tip: "When and how data must be deleted to meet the source's or the law's requirements." },
  { key: "policy_review_date", label: "Policy review date", type: "date", tip: "A recurring checkpoint so permissions don't silently go stale as sources change their terms." },
];
const REQUIRED = POLICY_FIELDS.map((f) => f.key);

function FieldLabel({ label, tip }: { label: string; tip: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {label}
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid={`policy-tip-${label}`} className="inline-flex text-slate-400 hover:text-slate-600 cursor-help"><HelpCircle className="h-3.5 w-3.5" /></span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs bg-slate-900 text-slate-50 leading-relaxed">{tip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
}

function PolicyRing({ completed, total, testid }: { completed: number; total: number; testid?: string }) {
  const size = 34, r = 13, stroke = 4;
  const circumference = 2 * Math.PI * r;
  const pct = total ? completed / total : 0;
  const complete = completed >= total;
  const color = complete ? "#0f766e" : pct >= 0.5 ? "#d97706" : "#e11d48";
  return (
    <span className="relative inline-flex items-center justify-center shrink-0" data-testid={testid}
      title={`${completed} of ${total} policy fields complete`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${circumference * pct} ${circumference}`}
          style={{ transition: "stroke-dasharray 500ms ease, stroke 300ms ease" }} />
      </svg>
      <span className="absolute text-[10px] font-bold num leading-none" style={{ color }}>{completed}/{total}</span>
    </span>
  );
}

export default function Connectors() {
  const { user } = useAuth();
  const [connectors, setConnectors] = useState<any[]>([]);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [templates, setTemplates] = useState<any[]>([]);
  const [tplName, setTplName] = useState("");

  const canEditPolicy = user?.role === "admin";

  const load = () => api.get("/connectors").then((r) => setConnectors(r.data));
  const loadTemplates = () => api.get("/connector-policy-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); loadTemplates(); }, []);

  const applyTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm(Object.fromEntries(POLICY_FIELDS.map((f) => [f.key, (t.policy || {})[f.key] || ""])));
    toast.success(`Loaded template "${t.name}"`);
  };
  const saveTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name the template first"); return; }
    try {
      const policy = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, (v || "").trim() || null]));
      await api.post("/connector-policy-templates", { name: tplName.trim(), policy });
      toast.success("Template saved"); setTplName(""); loadTemplates();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteTemplate = async (id: string) => {
    try { await api.delete(`/connector-policy-templates/${id}`); toast.success("Template deleted"); loadTemplates(); }
    catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const activate = async (key: string) => {
    try { await api.post(`/connectors/${key}/activate`); toast.success("Activated"); load(); }
    catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const missingCount = (c: any) => REQUIRED.filter((k) => !(c.policy || {})[k]).length;

  const openPolicy = (c: any) => {
    const p = c.policy || {};
    setForm(Object.fromEntries(POLICY_FIELDS.map((f) => [f.key, p[f.key] || ""])));
    setEditKey(c.key);
  };

  const savePolicy = async () => {
    try {
      const policy = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, (v || "").trim() || null]));
      await api.put(`/connectors/${editKey}/policy`, { policy });
      toast.success("Policy saved (logged to audit)");
      setEditKey(null);
      load();
    } catch (e: any) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const editingConnector = connectors.find((c) => c.key === editKey);

  return (
    <div className="space-y-5" data-testid="connectors-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><PlugZap className="h-5 w-5 text-teal-700" /> Source Connector Policies</h1>
        <p className="text-sm text-slate-500">Policy catalogue for Radar source connectors. Connector activation is governed independently by source authorisation, collection method and lifecycle phase.</p>
      </div>

      <div className="flex items-start gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        A connector cannot be activated without complete policy metadata (authorisation, owner, auth method, permitted purpose, retention, attribution, onward-use, deletion obligations, review date). Live ingestion is a Phase 02 capability.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {connectors.map((c) => {
          const automated = c.source_mode === "Automated";
          const miss = missingCount(c);
          return (
            <Card key={c.key} className="p-4 border-slate-200 shadow-none" data-testid={`connector-${c.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading font-semibold text-slate-800">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-md border whitespace-nowrap ${STATUS_STYLE[c.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{c.status}</span>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
                <span>Mode: <span className="font-medium text-slate-700">{c.source_mode}</span></span>
                <span>Phase: <span className="num">{c.phase}</span></span>
              </div>
              {c.parent_agent && (
                <div className="text-[11px] text-slate-500 mt-1" data-testid={`parent-agent-${c.key}`}>Parent Agent: <span className="font-medium text-slate-700">{c.parent_agent}</span></div>
              )}

              {automated && (
                <div className="mt-3 flex items-center gap-2.5 text-[11px]">
                  <PolicyRing completed={REQUIRED.length - miss} total={REQUIRED.length} testid={`policy-ring-${c.key}`} />
                  {miss === 0 ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`policy-complete-${c.key}`}><CheckCircle2 className="h-3.5 w-3.5" /> Policy complete — ready to activate in Phase 02</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`policy-incomplete-${c.key}`}><AlertCircle className="h-3.5 w-3.5" /> {miss} of {REQUIRED.length} field{miss === 1 ? "" : "s"} missing</span>
                  )}
                </div>
              )}

              <div className="mt-3 flex items-center gap-2">
                {c.key === "manual_entry" ? (
                  <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Active — analyst manual entry</span>
                ) : c.source_mode === "Manual Reference Only" ? (
                  <span className="text-xs text-red-600 flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Manual reference only — no automated access</span>
                ) : (
                  <>
                    {canEditPolicy && (
                      <Button size="sm" variant="outline" onClick={() => openPolicy(c)} data-testid={`edit-policy-${c.key}`}>
                        <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Edit policy
                      </Button>
                    )}
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} data-testid={`activate-wrap-${c.key}`}>
                            <Button size="sm" variant="outline" disabled={miss > 0} className="disabled:opacity-40 disabled:cursor-not-allowed" onClick={() => activate(c.key)} data-testid={`activate-${c.key}`}>
                              <Lock className="h-3.5 w-3.5 mr-1" /> Attempt activate
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {miss > 0 && (
                          <TooltipContent className="bg-slate-900 text-slate-50">
                            Complete the policy first — {miss} field{miss === 1 ? "" : "s"} missing.
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Policy editor */}
      <Dialog open={!!editKey} onOpenChange={(v) => !v && setEditKey(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="policy-dialog">
          <DialogHeader>
            <DialogTitle>Source usage policy — {editingConnector?.name}</DialogTitle>
            <DialogDescription>
              Record how this source may lawfully be used. Every field must be complete before the connector can be activated (a Phase 02 capability). Hover the <HelpCircle className="inline h-3 w-3" /> icon on each field for guidance. Saving is logged to the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="policy-templates">
            <div className="text-xs font-medium text-slate-600">Policy templates — reuse a saved set of answers to fill all 9 fields in one click</div>
            <div className="flex flex-wrap items-center gap-2">
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="h-8 w-60 bg-white" data-testid="template-load"><SelectValue placeholder={templates.length ? "Load a saved template…" : "No templates yet"} /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex items-center gap-1.5 ml-auto">
                <Input className="h-8 w-44 bg-white" placeholder="New template name" value={tplName} onChange={(e) => setTplName(e.target.value)} data-testid="template-name" />
                <Button size="sm" variant="outline" onClick={saveTemplate} data-testid="template-save"><Save className="h-3.5 w-3.5 mr-1" /> Save current</Button>
              </div>
            </div>
            {templates.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {templates.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded px-2 py-0.5">
                    {t.name}
                    <button onClick={() => deleteTemplate(t.id)} data-testid={`template-del-${t.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {POLICY_FIELDS.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label htmlFor={f.key}><FieldLabel label={f.label} tip={f.tip} /></Label>
                {f.type === "text" && (
                  <Input id={f.key} data-testid={`policy-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
                {f.type === "textarea" && (
                  <Textarea id={f.key} data-testid={`policy-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
                {f.type === "date" && (
                  <DatePicker testid={`policy-${f.key}`} value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} placeholder="Select review date" />
                )}
                {f.type === "select" && (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger id={f.key} data-testid={`policy-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKey(null)}>Cancel</Button>
            <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={savePolicy} data-testid="policy-save">Save policy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
